import type { Action, FormState } from "../types";
import { ValidationError } from "yup";
import sandboxSignupValidationSchema from "../validators/schemas/sandboxSignup";
import Link from "../../../signups/src/components/Link";
import React from "react";
import { CONTACT_SALES_LINK } from "../../../common/index";

export const initialState = {
  values: {
    firstName: "",
    lastName: "",
    companyName: "",
    businessLocation: { text: "", code: "", key: "" },
    emailAddress: "",
    consent: false
  },
  errors: {
    firstName: "",
    lastName: "",
    companyName: "",
    businessLocation: "",
    emailAddress: "",
    consent: ""
  }
};

export const validate = countryCode => (
  name: string,
  state: FormState
): string | void => {
  try {
    sandboxSignupValidationSchema.validateSyncAt(
      name,
      { ...state.values },
      { context: { countryCode } }
    );
  } catch (error) {
    if (ValidationError.isError(error)) {
      if (error.message === "Lesser volume") {
        return customLesserOnlineVolumeError;
      }
      return error.message;
    }

    throw error;
  }

  return undefined;
};

const customLesserOnlineVolumeError = (
  <React.Fragment>
    <span>
      Please{" "}
      <Link href={CONTACT_SALES_LINK} intent="simple" target="_blank">
        submit your contact information
      </Link>
      . Our sales team will reach out to you to discuss the best solution for
      your business.
    </span>
  </React.Fragment>
);

export const sandboxSignupReducerSwitch = (
  state: FormState,
  action: Action,
  countryCode: string
): FormState => {
  switch (action.type) {
    case "SANDBOX_SIGNUP_FIELD_CHANGE": {
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.name]: action.payload.value
        }
      };
    }
    case "SANDBOX_SIGNUP_FIELD_BLUR": {
      const sandboxSignupValidationError = validate(countryCode)(
        action.payload.name,
        {
          ...state
        }
      );
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.name]: action.payload.value
        },
        errors: {
          ...state.errors,
          [action.payload.name]: sandboxSignupValidationError
        }
      };
    }
    case "SANDBOX_SIGNUP_COUNTRY_CHANGE": {
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.name]: { ...action.payload.value }
        }
      };
    }
    case "SANDBOX_SIGNUP_COUNTRY_BLUR": {
      const sandboxSignupValidationError = validate(countryCode)(
        action.payload.name,
        {
          ...state
        }
      );
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.name]: { ...action.payload.value }
        },
        errors: {
          ...state.errors,
          [action.payload.name]: sandboxSignupValidationError
        }
      };
    }
    case "SANDBOX_SIGNUP_CHECKBOX_CHANGE": {
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.name]: action.payload.checked
        }
      };
    }
    case "SANDBOX_SIGNUP_CHECKBOX_BLUR": {
      return {
        ...state,
        values: {
          ...state.values,
          [action.payload.name]: action.payload.checked
        }
      };
    }
    case "SERVER_SIDE_VALIDATION_ERROR": {
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload
        }
      };
    }
    default: {
      return state;
    }
  }
};

const sandboxSignupReducer = (
  countryCode: string,
  countries: Array<string>
) => (state: FormState = initialState, action: Action): FormState =>
  sandboxSignupReducerSwitch(state, action, countryCode, countries);

export default sandboxSignupReducer;
